// we have to expose wavesurfer on the global scope
import tippy from 'expose-loader?tippy!tippy.js/dist/tippy.js';
import _ from 'expose-loader?_!lodash';
import util from '../utils/util';
import RecordingSelect from '../specials/RecordingSelect';
import AssignmentSelectSimple from '../specials/AssignmentSelectSimple';
import SpeakerLabel from '../forms/SpeakerLabel';
import RecordingVibes from '../layout/RecordingVibes';
import RecordingStatus from '../layout/RecordingStatus';
import RecordingMetrics from '../layout/RecordingMetrics';
import RecordingMetricsConversational from '../layout/RecordingMetricsConversational';
import FeedbackForm from '../forms/FeedbackForm';
import EvaluateTips from '../layout/EvaluateTips';
import CommentSticky from '../forms/CommentSticky';

export default {
  components: { CommentSticky, RecordingSelect, SpeakerLabel, RecordingVibes, RecordingStatus, RecordingMetrics,
    RecordingMetricsConversational, FeedbackForm, EvaluateTips, AssignmentSelectSimple },
  data() {
    return {
      allKey: 1,
      rightToolbarRef: 1,
      leftToolbarRef: 1,
      mainKey: 'main1',
      statusKey: 'status1',
      vibesKey: 'vibes1',
      chartsKey: 'charts1',
      tipsKey: 'tips1',
      stickyKey: 'sticky1',
      scorecardKey: 'scorecard1',

      recording: null,
      activeGroup: null,
      feedbackActive: false,
      videoEnabled: false,
      sharedGroup: '',
      sharedUser: '',
      retriedSharedUser: false,
      hotSpots: [],
      wavesurfer: null,
      vv: null,
      renderedCharts: null,
      speakerNum: 0,
    };
  },
  watch: {
    recording: {
      handler: 'playbackUrlChanged',
      deep: true,
    },
    'recording.playbackUrl': 'playbackUrlChanged',
    'recording.videoUrl': 'playbackUrlChanged',
    recordingId: 'fetchRecording',
    recordings() {
      this.setRecording();
    }
  },
  computed: {
    filteredAssignments: {
      cache: false,
      get() {
        return this._.filter(this.$store.getters.assignedAssignments, (ass) => {
          return !ass.archived;
        });
      },
    },
    commentCount: {
      cache: false,
      get() {
        const feedback = this.$store.getters.feedback;
        if (!feedback || !feedback[this.recording.recordingId]) return 0;
        return this._.filter(
          feedback[this.recording.recordingId],
          (f) => f.comment
        ).length;
      }
    },
    isReviewed: {
      cache: false,
      get() {
        const feedback = this.$store.getters.feedback;
        if (!feedback || !feedback[this.recording.recordingId]) return false;
        return !!(this._.filter(
          feedback[this.recording.recordingId],
          (f) => f.isManager
        ).length);
      }
    },
    showSharedWithGroup() {
      if (this.isShared) return false;

      if (this.assignmentsEnabled) {
        // Private recordings cannot be shared
        if (!this.recording.assignmentId) return false;
      }

      // Assignment recordings and non-assignment-enabled recordings are reliant on group settings
      return (this.activeGroup.speakerPrivacyLevel > 0 && this.activeGroup.speakerPrivacyLevel < 4);
    },
    showSubmittedForReview() {
      if (this.isShared) return false;

      if (this.assignmentsEnabled) {
        // Private recordings cannot be flagged
        if (!this.recording.assignmentId) {
          return false;
        }
        return true;
      }

      // When assignments are off, the group setting determines visibility
      return (this.activeGroup.managerPrivacyLevel > 0);
    },
    reportPrefix() {
      return !this.isConversational ? 'Public Speaking Assessment Report' : 'Assessment Report';
    },
    feedbackRegions: {
      cache: false,
      get() {
        const allFeedback = this._.cloneDeep(this.$store.getters.feedback);
        const feedback = (this.recording && allFeedback && allFeedback[this.recording.recordingId]) ?
          allFeedback[this.recording.recordingId] : [];
        return this._.filter(feedback, (f) => f.comment && f.regionStart);
      }
    },
    isSmallViewport: {
      cache: false,
      get: util.isSmallViewport
    },
    isMediumViewport: {
      cache: false,
      get: util.isMediumViewport
    },
    isLargeViewport: {
      cache: false,
      get: util.isLargeViewport
    },
    recordingId() {
      return this.$route.params.recordingId;
    },
    sharedRecordings() {
      return this.$store.getters.sharedRecordings;
    },
    isGroupManager: {
      cache: false,
      get() {
        return this._.includes(this.activeGroup.roles, 'manager');
      }
    },
    isMyRecording: {
      cache: false,
      get() {
        return this.recording.userId === this.$store.getters.user.userId;
      }
    },
    activeGroups() {
      return this.$store.getters.activeGroups;
    },
    routerLink() {
      if (!this.assignmentsEnabled) return { name: 'manage' };

      if (this.currentMode == 'speaker') {
        if (this.$route.query && typeof this.$route.query.assignmentId !== 'undefined') {
          return { name: 'assignment-details', params: {assignmentId: this.$route.query.assignmentId } };
        }
        return { name: 'speaker-dashboard' };
      }
      if (this.$route.query && typeof this.$route.query.assignmentId !== 'undefined') {
        return { name: 'preview-assignment', params: {assignmentId: this.$route.query.assignmentId } };
      }
      return { name: 'coach-dashboard' };
    },
    org() {
      return this.$store.getters.org;
    },
    preferences() {
      return this.$store.getters.preferences;
    },
    assignmentsEnabled() {
      return (this.org && typeof this.org.settings !== 'undefined' && this.org.settings.Assignments);
    },
    isAManager() {
      if (!this.$store.getters.groups.length) {
        return false;
      }
      const filter = group => {
        return this._.includes(group.roles, 'manager') && group.membershipStatus === 'active';
      };
      let group = this._.find(this.$store.getters.groups, filter);
      return !!group;
    },
    currentMode() {
      if (!this.isAManager) return 'speaker';
      return this.preferences.currentMode || 'manager';
    },
    isIe() {
      return (navigator.appName == 'Microsoft Internet Explorer' || !!navigator.userAgent.match(/Trident/g) || !!navigator.userAgent.match(/MSIE/g));
    },
    isVideo: {
      cache: false,
      get() {
        if (!this.recording || !this.recording.isVideo) {
          // Non-videos are always not video
          return false;
        }

        if (this.preferences && this.preferences[this.recording.recordingId] && this.preferences[this.recording.recordingId].hideVideo) {
          // Videos with a saved hideVideo preference are not videos
          return false;
        }

        // Other videos ARE videos
        return true;
      },
    },
    isShared() {
      return !!(this.recording.userId !== this.user.userId);
    },
    recordings() {
      return this.$store.getters.recordings;
    },
    scores() {
      return this.recording.results.scores;
    },
    tips() {
      return this.recording.results.tips;
    },
    timestamped() {
      return this.recording.results.timestamped;
    },
    user() {
      return this.$store.getters.user;
    },
    videoPlayerLoaded: {
      cache: false,
      get() {
        return (document.getElementById('videoPlayer'));
      }
    },
    hasVideoPlaybackData: {
      cache: false,
      get() {
        return (this.recording && this.recording.isVideo && this.recording.videoUrl);
      }
    },
    hasPlaybackData: {
      cache: false,
      get() {
        return (!this.isVideo && this.recording && this.recording.playbackUrl) || (this.isVideo && this.recording && this.recording.videoUrl);
      }
    },
    wavesurferContainerClass: {
      cache: false,
      get() {
        let classes = '';
        if (this.isVideo && this.hasVideoPlaybackData) {
          classes +=  'videoEnabled wsSpeakers1';
        } else {
          classes += `wsSpeakers${this.speakerCounts.length}`;
        }

        return classes;
      }
    },
    words() {
      return _.filter(_.map(this.scores.wordSpotting, (count, word) => {
        return {word, count};
      }), word => word.count > 0);
    },
    isConversational() {
      return !!(this.recording && this.recording.results && this.recording.results.isConversational);
    },
    resultsLoaded() {
      return (this.recording && this.recording.results && this.scores
        && this.tips
        && this.timestamped) || (this.recording && this.recording.results && this.recording.results.data);
    },
    speakerCounts() {
      if (!this.isConversational) return [1];

      let speakers = [];
      for (let i=1; i <= this.recording.results.numSpeakers; i++) {
        speakers.push(i);
      }
      return speakers;
    }
  },
  updated() {
    if (!this.resultsLoaded) {
      window.setTimeout(() => {
        this.$announcer.set('This recording is still processing.');
      }, 1000);
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.reset();
  },
  created() {
    const promises = [this.$store.dispatch('listRecordings')];
    if (this.assignmentsEnabled) {
      promises.push(this.$store.dispatch('getAssignedAssignments'));
    }

    window.Promise.all(promises)
      .then(() => {
        this.fetchRecording();
      });

    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
  },
  methods: {
    toggleVideoVisibility(hideVideo=true) {
      // Persist state
      this.$store.dispatch(
        'updatePreferences',
        Object.assign(this.preferences, {[this.recording.recordingId]: {hideVideo}})
      );

      this.incrementStatus();
      this.$forceUpdate();
      window.setTimeout(() => this.loadWavesurfer(true), 1000);
      return true;
    },
    feedbackModified() {
      this.incrementScorecard();
      this.rightToolbarModified();
    },
    rightToolbarModified() {
      this.rightToolbarRef++;
      this.$forceUpdate();
    },
    leftToolbarModified() {
      this.leftToolbarRef++;
      this.$forceUpdate();
    },
    identicalUrls(url1, url2) {
      const idx1 = url1.indexOf('?');
      const idx2 = url2.indexOf('?');
      return (url1.substring(0, idx1) === url2.substring(0, idx2));
    },
    playbackUrlChanged() {
      // Before we do anything, check if video playback just became available on a fresh recording
      if (this.isVideo && this.hasVideoPlaybackData && !this.videoPlayerLoaded) {
        this.incrementStatus();
        this.$forceUpdate();
        this.$nextTick(() => this.loadWavesurfer(true));
        return true;
      }

      this.incrementStatus();

      if (!this.hasPlaybackData) return;

      // We need to manually trigger Wavesurfer for new recordings
      // because the playbackUrl loads in without triggering the watcher
      if (!this.wavesurferLoaded && !this.renderedWavesurfer && this.hasPlaybackData) {
        this.$nextTick(() => {
          // Hack because incrementStatus() doesnt actually trigger a re-render
          if (document.getElementById('extractingAudio') && this.recording.playbackUrl) {
            document.getElementById('extractingAudio').innerHTML = 'Computing results...';
          }
          this.loadWavesurfer();
        });
      }

      // We need to re-render Wavesurfer for conversational recordings
      // when the single channel audio is swapped for multichannel
      if (this.wavesurferLoaded && this.recording.recordingId === this.renderedWavesurfer && !this.identicalUrls(this.wavesurferUrl, this.recording.playbackUrl)) {
        this.loadWavesurfer(true);
      }
    },
    // METHODS DEALING WITH PAGE LOAD
    retrievalFailed() {
      // Do not show the error if the user already navigated away
      if (this.$router.currentRoute.name.indexOf('evaluate') === -1) return;

      window.setTimeout(() => {
        if (this.$router.currentRoute.name.indexOf('evaluate') === -1) return;
        this.$store.dispatch('notify', {
          type: 'error',
          subject: 'Error Retrieving Recording',
          text: 'This recording could not be retrieved. Most likely, it has either been deleted ' +
            'by the owner or you no longer have permission to view it.',
        });
      }, 2000);
      return this.$router.replace({name: 'evaluate'});
    },
    refresh() {
      this.$nextTick(() => { window.location.reload(); });
    },
    fetchRecording() {
      // If we're being asked to fetch a falsy value, redirect back to evaluate
      if (!this.recordingId) {
        return this.$router.replace({name: 'evaluate'});
      }

      if (this.recording && this.recording.processing && this.recordingId === this.recording.recordingId) {
        return; // no fetch - auto pulling of results is done by the store if it's processing
      }

      // Tell the UI that we are loading. I don't think this does anything really...
      this.wavesurferLoaded = false;

      // fetch will trigger a set when recordings list changes
      this.$store.dispatch('getRecording',
        {recordingId: this.recordingId})
        .then(this.setRecording)
        .then(() => {
          // Check for a 404. I'm not sure why 404s no longer throw...
          if (!this.recording) {
            return this.retrievalFailed();
          }
        })
        .catch(() => {
          return this.retrievalFailed();
        });
    },
    setRecording() {
      // Look for the recording under the user, then under sharedRecordings
      this.recording = this._.find(this.recordings, r => r.recordingId === this.recordingId);
      if (!this.recording) {
        this.recording = this._.find(this.sharedRecordings, r => r.recordingId === this.recordingId);
      }

      // Decide whether to show feedback
      this.feedbackActive = false;
      if (this.recording) {
        this.activeGroups.forEach((group) => {
          if (group.groupId === this.recording.groupId) {
            this.activeGroup = group;
            if (group.speakerPrivacyLevel > 0) {
              this.feedbackActive = true;
            }
          }
        });
      }

      // Now that the recording is fetched, render it
      if (this.recording && this.recording.speakerNum) {
        this.speakerNum = this.recording.speakerNum - 1;
      } else {
        this.speakerNum = 0;
      }
      this.retriedSharedUser = false;
      this.incrementKeys();
      this.incrementMain();
      this.loadSharedData();
    },

    loadRecording(option) {
      if (!option || !option.recordingId) {
        return;
      }
      const recordingId = option.recordingId;
      this.speakerNum = 0;
      this.$router.push({ name: 'evaluate-recording', params: { recordingId } });
    },
    skipToSummary() {
      document.getElementById('summarylink').click();
      this.$nextTick(() => document.getElementById('scoreBookmarkForScreenReader').focus());
    },
    handleResize() {
      if (!this.recording) return;

      if (this.isVideo && !this.wavesurferLoading && this.wavesurferLoaded) {
        const vP = document.getElementById('videoPlayer');
        if (!this.recording.playbackUrl && this.isVideo && vP && 'video' !== vP.nodeName.toLowerCase()) {
          // Exit if the video isnt loaded yet
          return;
        }

        const videoContainer = document.getElementById('videoPlayerContainer');
        const videoPlayerMain = document.getElementById('videoPlayerMain');
        const videoPlayerSidebar = document.getElementById('videoPlayerSidebar');
        const videoPlayerToggle = document.getElementById('closeVideoContainer');

        // Make sure the videoPlayer is in the correct position
        if (videoContainer && videoPlayerMain && (util.isSmallViewport() || util.isMediumViewport())) {
          if (videoContainer.parentNode !== videoPlayerMain) {
            // Main container needs fill layout
            if (videoPlayerToggle) videoPlayerMain.appendChild(videoPlayerToggle);
            videoPlayerMain.appendChild(videoContainer);
          }
        } else {
          if (videoContainer && videoPlayerSidebar && videoContainer.parentNode !== videoPlayerSidebar) {
            // Sidebar needs fluid layout
            if (videoPlayerToggle) videoPlayerSidebar.appendChild(videoPlayerToggle);
            videoPlayerSidebar.appendChild(videoContainer);
          }
        }
      }
    },
    handleScroll() {
      // We do not fix wavesurfer on mobile, video or when there are more than 3 speakers
      let dontFix = !!(this.isSmallViewport || (this.speakerCounts.length >= 4) || (this.isVideo && !util.isLargeViewport()));

      // We also dont fix if there is not enough room to show all the metrics in the remaining space
      const viewportHeight = util.getViewportHeight();
      const threshold = (this.speakerCounts.length === 3) ? 750 : 600;
      if (viewportHeight < threshold) dontFix = true;

      let wavesurferContainer = document.getElementById('wavesurferContainer');
      if (!wavesurferContainer) return;

      if (window.pageYOffset >= wavesurferContainer.offsetTop + 80 && !dontFix) {
        if (!wavesurferContainer.classList.contains('sticky')) {
          wavesurferContainer.style.width = wavesurferContainer.offsetWidth + 'px';
          wavesurferContainer.classList.add('sticky');
        }
      } else if (wavesurferContainer.classList.contains('sticky')) {
        wavesurferContainer.style.width = null;
        wavesurferContainer.classList.remove('sticky');

        // Fire a resize event to trigger comment sticky repositioning
        window.dispatchEvent(new Event('resize'));
      }
    },

    incrementStatus() {
      this.allKey++;
      this.statusKey = `status${this.mainKey++}`;
    },

    incrementMain() {
      this.allKey++;
      this.mainKey = `vibes${this.mainKey++}`;
      this.statusKey = `status${this.mainKey++}`;
    },

    incrementSticky() {
      this.allKey++;
      this.stickyKey = `sticky${this.mainKey++}`;
    },

    incrementScorecard() {
      this.allKey++;
      this.scorecardKey = `scorecard${this.allKey++}`;
    },

    incrementKeys() {
      this.allKey++;
      this.stickyKey = `sticky${this.mainKey++}`;
      this.statusKey = `status${this.mainKey++}`;
      this.vibesKey = `vibes${this.allKey++}`;
      this.chartsKey = `charts${this.allKey++}`;
      this.tipsKey = `tips${this.allKey++}`;
      this.scorecardKey = `scorecard${this.allKey++}`;
    },

    speakerChanged() {
      // Force the charts and summary to refresh
      this.incrementKeys();
    },

    // TODO: loadSharedData() was needed because having sharedGroup and sharedUser as computed props would not refresh
    // them after dispatch('listGroupMembers') so it needed to be done manually
    loadSharedData() {
      if (!this.recording) return false;

      const group = _.find(this.$store.getters.groups, {'groupId': this.recording.groupId});
      if (group) {
        this.sharedGroup = group.name;
      }

      const user = _.find(this.$store.getters.groupMembers[this.recording.groupId], {'userId': this.recording.userId});
      if (!user && !this.retriedSharedUser) {
        this.$store.dispatch('listGroupMembers').then(() => {
          this.retriedSharedUser = true;
          this.loadSharedData();
        });
      } else {
        this.sharedUser = (user && user.name) ? user.name : '';
        this.$nextTick(this.loadWavesurfer);
      }
    },
    speakerIdentified(speakerNum) {
      let payload = this._.cloneDeep(this.recording);
      payload.speakerNum = speakerNum;
      this.$store.dispatch(
        'updateSpeakerNum',
        payload
      );
    },
    printUnlessTab(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        return true;
      }
      window.print();
    },
    hasHotSpots(key) {
      return this.timestamped && this.timestamped[key] &&
        this.timestamped[key].hotSpots && this.timestamped[key].hotSpots.length > 0;
    },
    setHotSpots(hotSpots) {
      this.hotSpots = hotSpots;
    },
    updateRecording(data) {
      this.$store.dispatch('updateRecording', data).then(() => this.rightToolbarModified());
    },
    moveRecording(assignment) {
      if (assignment && assignment.criteria &&
        assignment.criteria.algorithm && this.recording.results &&
        this.recording.results.algorithm
      ) {
        const assignmentAlgorithmFeatureName = assignment.criteria.algorithm.split('-')[1];
        const recordingAlgorithmFeatureName = this.recording.results.algorithm.split('-')[1];
        if (assignmentAlgorithmFeatureName !== recordingAlgorithmFeatureName) {
          return this.$store.dispatch('notify', {
            type: 'error',
            subject: 'Unable to Use This Assignment',
            text: 'This assignment only accepts recordings with a single speaker and ' +
              'this recording contains multiple speakers (or vice versa).'
          });
        }
      }
      if (assignment && window.confirm(`Are you sure you want to assign this recording to "${assignment.name}"? This operation cannot be undone.`)) {
        this.$store.dispatch(
          'addRecordingToAssignment',
          {
            recordingId: this.recording.recordingId,
            assignmentId: assignment.assignmentId,
          }
        ).then(
          () => {
            this.$store.dispatch('getRecording',
              {recordingId: this.recordingId})
              .then(
                () => {
                  this.leftToolbarModified();
                  this.rightToolbarModified();
                });
          });
      }
    },
  }
};